import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, globalHistory } from "@reach/router";
import { useFileUploadContext } from "./FileUploadContext";

/**
 * Hook that prevents browser navigation, route changes, and modal closures during uploads.
 * Combines @reach/router blocking, beforeunload blocking, and a guard function for modal close.
 */
export function usePreventAllNavigationDuringUpload(
	customMessage = "Files are still uploading. Are you sure you want to leave this page (and cancel uploads)?"
) {
	const location = useLocation();
	const { cancelAllUploads, isUploading } = useFileUploadContext();
	const isBlockingRef = useRef(false);
	const [hasUserCancelled, setHasUserCancelled] = useState(false);

	// Block @reach/router navigation
	useEffect(() => {
		const unblock = globalHistory.listen(({ location: newLocation }) => {
			if (isUploading && !isBlockingRef.current) {
				const shouldBlock = !window.confirm(customMessage);

				if (shouldBlock) {
					isBlockingRef.current = true;
					globalHistory.navigate(location.pathname, { replace: true });
					setTimeout(() => {
						isBlockingRef.current = false;
					}, 0);
				} else {
					// user wants to leave, cancel any uploads
					cancelAllUploads();
					setHasUserCancelled(true);
				}
			}
		});

		return unblock;
	}, [isUploading, location.pathname, customMessage, cancelAllUploads]);

	// Block browser navigation (page close/refresh)
	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (isUploading) {
				e.preventDefault();
				return (e.returnValue = "");
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [isUploading]);

	/**
	 * Guard function for modal/dialog closures. Call this before closing.
	 * Returns true if safe to close, false if the user chose to stay.
	 */
	const isSafeToClose = useCallback((): boolean => {
		if (!isUploading) {
			return true;
		}

		const shouldLeave = window.confirm(customMessage);

		if (shouldLeave) {
			cancelAllUploads();
			setHasUserCancelled(true);
			return true;
		}

		return false;
	}, [isUploading, customMessage, cancelAllUploads]);

	return { isSafeToClose, hasUserCancelled };
}
